import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, ListGroup, Modal, Row, Spinner, Tab, Tabs, Table } from 'react-bootstrap';
import axios from '../Config/axiosConfig';
import EditorAviso from './EditorAviso';
import ModalAviso from './ModalAviso';
import AvaliacoesSistema from './AvaliacoesSistema';
import { avisosApi, dataParaCampo, erroAviso, MAX_CONTEUDO_AVISO, statusAviso, temConteudoAviso } from '../Shared/Utils/avisos';
import '../Shared/sharedStyles.css';
import './Configuracoes.css';

const novoAviso = () => ({ titulo: '', conteudoHtml: '', inicio: dataParaCampo(Date.now()),
  fim: dataParaCampo(Date.now() + 86400000), ativado: false });
const formatarData = value => new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

export default function Configuracoes() {
  const [avisos, setAvisos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [falhaCarregamento, setFalhaCarregamento] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [editorKey, setEditorKey] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [preview, setPreview] = useState(null);
  const [dispensas, setDispensas] = useState(null);
  const [now, setNow] = useState(Date.now());

  const carregar = useCallback(async () => {
    setLoading(true);
    setFalhaCarregamento(false);
    try { const response = await axios.get(avisosApi); setAvisos(response.data); setError(''); }
    catch (err) { setFalhaCarregamento(true); setError(erroAviso(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(timer); }, []);

  const editar = aviso => {
    setError(''); setSuccess('');
    setForm(aviso ? { ...aviso, inicio: dataParaCampo(aviso.inicio), fim: dataParaCampo(aviso.fim) } : novoAviso());
    setEditorKey(key => key + 1);
  };
  const alterar = (field, value) => setForm(previous => ({ ...previous, [field]: value }));

  const salvar = async event => {
    event.preventDefault();
    setError(''); setSuccess('');
    if (!temConteudoAviso(form.conteudoHtml)) {
      setError('Escreva uma mensagem ou inclua uma imagem.'); return;
    }
    const inicio = new Date(form.inicio), fim = new Date(form.fim);
    if (!Number.isFinite(inicio.getTime()) || !Number.isFinite(fim.getTime()) || fim <= inicio) {
      setError('Informe as datas. O fim deve ser posterior ao início.'); return;
    }
    if (form.conteudoHtml.length > MAX_CONTEUDO_AVISO) {
      setError('O aviso está muito grande. Reduza a quantidade ou o tamanho das imagens.'); return;
    }
    setSaving(true);
    try {
      const body = { titulo: form.titulo?.trim() || '', conteudoHtml: form.conteudoHtml,
        inicio: inicio.toISOString(), fim: fim.toISOString(), ativado: form.ativado };
      const response = form.id ? await axios.put(`${avisosApi}/${form.id}`, body) : await axios.post(avisosApi, body);
      setAvisos(previous => [response.data, ...previous.filter(item => item.id !== response.data.id)]);
      setForm(null);
      setSuccess('Aviso salvo com sucesso.');
    } catch (err) { setError(erroAviso(err)); }
    finally { setSaving(false); }
  };

  return <Container fluid className="configuracoes-page py-3">
    <Card>
      <Card.Header className="title-card-custom"><h4 className="mb-0">Configurações</h4></Card.Header>
      <Card.Body>
        <Tabs defaultActiveKey="avisos" id="configuracoes-abas" className="mb-4">
          <Tab eventKey="avisos" title="Avisos aos clientes">
            <p className="text-muted">Crie mensagens para os clientes visualizarem ao acessar o portal. A exibição respeita o período e a ativação de cada aviso.</p>
            {error && <Alert variant="danger" role="alert">{error}</Alert>}
            {success && <Alert variant="success" role="status">{success}</Alert>}
            {form ? <Form onSubmit={salvar}>
              <h5 className="mb-3">{form.id ? 'Editar aviso' : 'Novo aviso'}</h5>
              <fieldset disabled={saving}>
                <Form.Group controlId="aviso-titulo" className="mb-3">
                  <Form.Label>Título (opcional)</Form.Label>
                  <Form.Control autoFocus maxLength={160} value={form.titulo || ''} onChange={e => alterar('titulo', e.target.value)} />
                </Form.Group>
                <Row className="g-3 mb-3">
                  <Col md={4}><Form.Group controlId="aviso-inicio">
                    <Form.Label>Início *</Form.Label>
                    <Form.Control type="datetime-local" required value={form.inicio} onChange={e => alterar('inicio', e.target.value)} />
                  </Form.Group></Col>
                  <Col md={4}><Form.Group controlId="aviso-fim">
                    <Form.Label>Fim *</Form.Label>
                    <Form.Control type="datetime-local" required min={form.inicio} value={form.fim} onChange={e => alterar('fim', e.target.value)} />
                  </Form.Group></Col>
                  <Col md={4} className="d-flex align-items-end pb-2">
                    <Form.Check type="switch" id="aviso-ativado" label={form.ativado ? 'Ativado' : 'Desativado'} checked={form.ativado} onChange={e => alterar('ativado', e.target.checked)} />
                  </Col>
                </Row>
                <p className="text-muted small">Datas e horários no fuso do seu dispositivo ({Intl.DateTimeFormat().resolvedOptions().timeZone}). O aviso deixa de aparecer no horário de fim.</p>
                <Form.Label htmlFor="conteudo-aviso">Mensagem *</Form.Label>
                <EditorAviso key={editorKey} initialHtml={form.conteudoHtml} disabled={saving} onChange={html => alterar('conteudoHtml', html)} />
                <div className="d-flex flex-wrap gap-2 mt-3">
                  <Button type="submit" variant="success">{saving ? 'Salvando…' : 'Salvar aviso'}</Button>
                  <Button type="button" variant="outline-primary" onClick={() => setPreview(form)}>Pré-visualizar</Button>
                  <Button type="button" variant="secondary" onClick={() => { setForm(null); setError(''); }}>Cancelar</Button>
                </div>
              </fieldset>
            </Form> : <>
              <div className="d-flex gap-2 mb-3">
                <Button variant="success" onClick={() => editar(null)}>Novo aviso</Button>
                <Button variant="outline-secondary" onClick={carregar} disabled={loading}>Atualizar</Button>
              </div>
              {loading ? <div className="py-5 text-center" role="status"><Spinner animation="border" size="sm" /> Carregando avisos…</div>
                : falhaCarregamento ? <div className="aviso-vazio">A lista de avisos não foi carregada. Clique em “Atualizar” para tentar novamente.</div>
                : avisos.length === 0 ? <div className="aviso-vazio">Nenhum aviso cadastrado. Clique em “Novo aviso” para começar.</div>
                  : <Table responsive hover className="align-middle">
                    <thead><tr><th>Título</th><th>Início</th><th>Fim</th><th>Situação</th><th>Não exibir</th><th>Alterado por</th><th>Ações</th></tr></thead>
                    <tbody>{avisos.map(aviso => {
                      const status = statusAviso(aviso, now);
                      return <tr key={aviso.id}>
                        <td className="aviso-titulo-celula">{aviso.titulo || 'Sem título'}</td><td>{formatarData(aviso.inicio)}</td><td>{formatarData(aviso.fim)}</td>
                        <td><Badge bg={status.cor}>{status.texto}</Badge></td>
                        <td>{aviso.dispensas?.length ? <Button size="sm" variant="link" className="p-0" onClick={() => setDispensas(aviso)}>
                          {aviso.dispensas.length} {aviso.dispensas.length === 1 ? 'cliente' : 'clientes'}
                        </Button> : 'Nenhum'}</td><td>{aviso.alteradoPor}</td>
                        <td><div className="d-flex gap-2"><Button size="sm" variant="outline-primary" onClick={() => editar(aviso)}>Editar</Button>
                          <Button size="sm" variant="outline-secondary" onClick={() => setPreview(aviso)}>Visualizar</Button></div></td>
                      </tr>;
                    })}</tbody>
                  </Table>}
            </>}
          </Tab>
          <Tab eventKey="avaliacoes" title="Avaliações do sistema"><AvaliacoesSistema /></Tab>
        </Tabs>
      </Card.Body>
    </Card>
    <ModalAviso aviso={preview} preview onClose={() => setPreview(null)} />
    <Modal show={Boolean(dispensas)} onHide={() => setDispensas(null)} centered>
      <Modal.Header closeButton><Modal.Title>Clientes que não querem rever</Modal.Title></Modal.Header>
      <Modal.Body>
        <p className="text-muted">{dispensas?.titulo || 'Aviso aos clientes'}</p>
        <ListGroup>{dispensas?.dispensas?.map(item => <ListGroup.Item key={`${item.idCliente}:${item.dispensadoEm}`}>
          <div className="fw-semibold">{item.usuarioCliente || `Cliente ${item.idCliente}`}</div>
          <small className="text-muted">Cliente #{item.idCliente} · {formatarData(item.dispensadoEm)}</small>
        </ListGroup.Item>)}</ListGroup>
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" onClick={() => setDispensas(null)}>Fechar</Button></Modal.Footer>
    </Modal>
  </Container>;
}
